import type { CSSProperties } from 'react';
import { isRecitationMeeting } from '@/app/lib/meetingTypes';

import { formatBaseIdDisplay } from '@/app/lib/utils';
import type { ScheduledCourse } from '@/app/types/course';
import { readableCourseTitle } from '@/app/lib/courseTitle';

export interface ScheduleCoursePreviewPosition {
  left: number;
  top: number;
}

interface ScheduleCoursePreviewProps {
  course: ScheduledCourse;
  isOpen?: boolean;
  showAvailability?: boolean;
  professorRating?: number | null;
  professorDifficulty?: number | null;
  id?: string;
  className?: string;
  style?: CSSProperties;
}

const PREVIEW_WIDTH = 304;
const PREVIEW_HEIGHT_ESTIMATE = 400;
const PREVIEW_GAP = 10;
const VIEWPORT_PADDING = 12;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function getScheduleCoursePreviewPosition(
  anchor: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom' | 'width'>,
  viewport: { width: number; height: number },
  placement: 'vertical' | 'side' = 'vertical',
): ScheduleCoursePreviewPosition {
  const maxLeft = viewport.width - PREVIEW_WIDTH - VIEWPORT_PADDING;
  const maxTop = viewport.height - PREVIEW_HEIGHT_ESTIMATE - VIEWPORT_PADDING;

  if (placement === 'side') {
    const fitsRight = anchor.right + PREVIEW_GAP + PREVIEW_WIDTH <= viewport.width - VIEWPORT_PADDING;
    const preferredLeft = fitsRight
      ? anchor.right + PREVIEW_GAP
      : anchor.left - PREVIEW_WIDTH - PREVIEW_GAP;

    return {
      left: clamp(preferredLeft, VIEWPORT_PADDING, maxLeft),
      top: clamp(anchor.top, VIEWPORT_PADDING, maxTop),
    };
  }

  const fitsAbove = anchor.top - PREVIEW_GAP >= PREVIEW_HEIGHT_ESTIMATE;
  const preferredTop = fitsAbove
    ? anchor.top - PREVIEW_HEIGHT_ESTIMATE - PREVIEW_GAP
    : anchor.bottom + PREVIEW_GAP;

  return {
    left: clamp(anchor.left + anchor.width / 2 - PREVIEW_WIDTH / 2, VIEWPORT_PADDING, maxLeft),
    top: clamp(preferredTop, VIEWPORT_PADDING, maxTop),
  };
}

function formatTime(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${String(mins).padStart(2, '0')} ${period}`;
}

function formatClassIndex(value?: string | number | null) {
  if (value === undefined || value === null) return 'N/A';
  const normalized = String(value).trim();
  if (!normalized) return 'N/A';
  return /^\d+$/.test(normalized) ? normalized.padStart(5, '0') : normalized;
}

function formatCampus(course: ScheduledCourse) {
  const campuses = (course.campuses ?? []).map((campus) => campus?.trim()).filter(Boolean);
  return campuses.length > 0 ? Array.from(new Set(campuses)).join(', ') : course.campus || 'Campus TBA';
}

function instructorNames(course: ScheduledCourse) {
  return course.instructors
    .map((instructor) => typeof instructor === 'string' ? instructor : instructor.name)
    .filter(Boolean)
    .join(', ');
}

export default function ScheduleCoursePreview({
  course,
  isOpen,
  showAvailability = true,
  professorRating,
  professorDifficulty,
  id,
  className = '',
  style,
}: ScheduleCoursePreviewProps) {
  const customActivity = course.id.startsWith('custom:') || course.department === 'CUSTOM';
  const professors = instructorNames(course);
  const campus = formatCampus(course);
  const meetings = course.meetingTimes?.filter((meeting) => meeting.day && meeting.day !== 'TBA') ?? [];

  return (
    <section
      id={id}
      role="tooltip"
      aria-label={`${course.name} course preview`}
      className={`w-[19rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-card border border-[var(--ss-overlay-border)] bg-[var(--ss-overlay-surface)] text-[var(--ss-text)] shadow-e3 animate-in fade-in duration-150 motion-reduce:animate-none ${className}`}
      style={style}
    >
      <div className="border-b border-[var(--ss-border)] p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold">{readableCourseTitle(course.name)}</h3>
            <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">
              {customActivity ? 'Custom activity' : `${formatBaseIdDisplay(course.baseId)} · Section ${course.section}`}
            </p>
          </div>
          {(customActivity || showAvailability) && <span className={`shrink-0 rounded-control px-2 py-1 text-[10px] font-semibold uppercase ${customActivity
            ? 'bg-[var(--ss-surface-subtle)] text-[var(--ss-text-muted)]'
            : isOpen
              ? 'bg-[var(--ss-positive-bg)] text-[var(--ss-positive)]'
              : 'bg-[var(--ss-critical-bg)] text-[var(--ss-critical)]'
          }`}>
            {customActivity ? 'Blocked' : isOpen ? 'Open' : 'Closed'}
          </span>}
        </div>

        {!customActivity ? (
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-[var(--ss-text-muted)]">
            <span className="rounded-control bg-[var(--ss-surface-subtle)] px-2 py-1">Index {formatClassIndex(course.id)}</span>
            {course.credits ? <span className="rounded-control bg-[var(--ss-surface-subtle)] px-2 py-1">{course.credits} credits</span> : null}
            <span className="rounded-control bg-[var(--ss-surface-subtle)] px-2 py-1">{campus}</span>
          </div>
        ) : null}
      </div>

      <div className="space-y-3 p-3">
        {!customActivity && professors && professors !== 'TBA' ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">Professor</p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold">{professors}</span>
              {typeof professorRating === 'number' ? (
                <span className="rounded-control bg-[var(--ss-surface-subtle)] px-2 py-0.5 font-semibold">{professorRating.toFixed(1)} rating</span>
              ) : null}
              {typeof professorDifficulty === 'number' ? (
                <span className="rounded-control bg-[var(--ss-surface-subtle)] px-2 py-0.5 text-[var(--ss-text-muted)]">{professorDifficulty.toFixed(1)} difficulty</span>
              ) : null}
            </div>
          </div>
        ) : null}

        {meetings.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">Meets</p>
            {meetings.slice(0, 4).map((meeting, index) => (
              <div key={`${course.id}-preview-meeting-${index}`} className="flex items-start justify-between gap-3 rounded-control bg-[var(--ss-surface-subtle)] px-2.5 py-2 text-xs">
                <div className="min-w-0">
                  <p className="font-semibold">{isRecitationMeeting(meeting) ? 'Recitation · ' : ''}{meeting.day}</p>
                  <p className="truncate text-[var(--ss-text-muted)]">{meeting.location || course.location || campus}</p>
                </div>
                <p className="shrink-0 text-right text-[var(--ss-text-muted)]">
                  {formatTime(meeting.startMinute)}–{formatTime(meeting.endMinute)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--ss-text-muted)]">No scheduled meeting time.</p>
        )}
      </div>
    </section>
  );
}
